/**
 * LVS font layout engine.
 * Pure TypeScript — no React imports.
 *
 * Given a string, font variant, and cap height h, computes the position
 * and scale for each character glyph.
 */

import {WIDTHS_A, WIDTHS_B} from './metrics'
import {
    KERN_A_UU,
    KERN_A_UL,
    KERN_A_LL,
    KERN_A_DD,
    KERN_A_DL,
    KERN_B_UU,
    KERN_B_UL,
    KERN_B_LL,
    KERN_B_DD,
    KERN_B_DL,
    DIGIT_UNIT_A,
    DIGIT_UNIT_B
} from './kerning'

// Stroke width divisor: approximate 1/7 of cap height
const STROKE_DIVISOR = 7

export type FontVariant = 'A' | 'B'

export interface GlyphPlacement {
    /** The character (key into GLYPHS_A/B) */
    char: string
    /** Left edge in output units (mm when h is in mm) */
    x: number
    /** Glyph advance width in output units */
    width: number
    /** Uniform scale: k = h / 350 */
    scale: number
}

export interface TextLayout {
    glyphs: GlyphPlacement[]
    /** Total advance width of the text */
    totalWidth: number
    /** Cap height = h */
    height: number
}

// ---------------------------------------------------------------------------
// Character classification helpers
// ---------------------------------------------------------------------------

const UPPERCASE_LATVIAN = new Set(['Ā', 'Č', 'Ē', 'Ģ', 'Ī', 'Ķ', 'Ļ', 'Ņ', 'Š', 'Ū', 'Ž'])
const LOWERCASE_LATVIAN = new Set(['ā', 'č', 'ē', 'ģ', 'ī', 'ķ', 'ļ', 'ņ', 'š', 'ū', 'ž'])

function isUppercase(ch: string): boolean {
    if (ch >= 'A' && ch <= 'Z') return true
    return UPPERCASE_LATVIAN.has(ch)
}

function isLowercase(ch: string): boolean {
    if (ch >= 'a' && ch <= 'z') return true
    return LOWERCASE_LATVIAN.has(ch)
}

function isDigit(ch: string): boolean {
    return ch >= '0' && ch <= '9'
}

// ---------------------------------------------------------------------------
// Latvian diacritic base-letter resolution (for kerning table lookup)
// ---------------------------------------------------------------------------

const DIACRITIC_MAP: Readonly<Record<string, string>> = {
    Ā: 'A',
    Č: 'C',
    Ē: 'E',
    Ģ: 'G',
    Ī: 'I',
    Ķ: 'K',
    Ļ: 'L',
    Ņ: 'N',
    Š: 'S',
    Ū: 'U',
    Ž: 'Z',
    ā: 'a',
    č: 'c',
    ē: 'e',
    ģ: 'g',
    ī: 'i',
    ķ: 'k',
    ļ: 'l',
    ņ: 'n',
    š: 's',
    ū: 'u',
    ž: 'z'
}

function resolveBase(ch: string): string {
    return DIACRITIC_MAP[ch] ?? ch
}

// ---------------------------------------------------------------------------
// Kerning table lookup
// ---------------------------------------------------------------------------

type KernTable = Readonly<Record<string, Readonly<Record<string, number>>>>

function lookupTable(table: KernTable, left: string, right: string): number | undefined {
    const row = table[resolveBase(left)]
    if (row === undefined) return undefined
    const val = row[resolveBase(right)]
    if (val === undefined) return undefined
    return val
}

/**
 * Returns the kerning (gap) between left and right characters, in font units
 * (at h=350). Returns undefined if no table entry is found.
 */
function kernLookup(left: string, right: string, variant: FontVariant): number | undefined {
    const UU = variant === 'A' ? KERN_A_UU : KERN_B_UU
    const UL = variant === 'A' ? KERN_A_UL : KERN_B_UL
    const LL = variant === 'A' ? KERN_A_LL : KERN_B_LL
    const DD = variant === 'A' ? KERN_A_DD : KERN_B_DD
    const DL = variant === 'A' ? KERN_A_DL : KERN_B_DL

    const lUp = isUppercase(left)
    const lLo = isLowercase(left)
    const lDig = isDigit(left)
    const rUp = isUppercase(right)
    const rLo = isLowercase(right)
    const rDig = isDigit(right)

    // 1. Both uppercase
    if (lUp && rUp) return lookupTable(UU, left, right)

    // 2. Uppercase → lowercase
    if (lUp && rLo) return lookupTable(UL, left, right)

    // 3. Both lowercase
    if (lLo && rLo) return lookupTable(LL, left, right)

    // 4. Digit → digit or punctuation
    if (lDig && (rDig || (!rUp && !rLo))) {
        const v = lookupTable(DD, left, right)
        if (v !== undefined) return v
    }

    // 5. Digit → lowercase
    if (lDig && rLo) {
        return lookupTable(DL, left, right)
    }

    // 6. Punctuation as left character — check UU, LL, DD (they include punct rows)
    if (!lUp && !lLo && !lDig) {
        const v1 = lookupTable(UU, left, right)
        if (v1 !== undefined) return v1
        const v2 = lookupTable(LL, left, right)
        if (v2 !== undefined) return v2
        const v3 = lookupTable(DD, left, right)
        if (v3 !== undefined) return v3
    }

    return undefined
}

// ---------------------------------------------------------------------------
// Digit→unit special spacing detection
// ---------------------------------------------------------------------------

/**
 * If the character at position `pos` in `chars` begins a known unit
 * abbreviation (m, km, t) at a "word boundary" (i.e. preceded by a digit),
 * returns the special spacing override in font units, otherwise undefined.
 */
function digitUnitSpacing(
    chars: string[],
    digitPos: number,
    unitPos: number,
    variant: FontVariant
): number | undefined {
    const DU = variant === 'A' ? DIGIT_UNIT_A : DIGIT_UNIT_B

    // Must be immediately after a digit
    if (!isDigit(chars[digitPos])) return undefined

    const ch = chars[unitPos]
    if (ch === undefined) return undefined

    // Check for 'km'
    if (ch === 'k' && chars[unitPos + 1] === 'm') {
        return DU.km
    }

    // Check for 'm' (metre) — but not 'm' that is part of 'km' or other combos
    if (ch === 'm') {
        return DU.m
    }

    // Check for 't' (tonne) — use the table value plus bonus
    // The base table spacing comes from the kerning table (DL: digit→lowercase t),
    // plus the t_bonus override
    if (ch === 't') {
        // Look up the normal digit→t spacing and add the bonus
        const base = kernLookup(chars[digitPos], 't', variant) ?? 350 / STROKE_DIVISOR
        return base + DU.t_bonus
    }

    return undefined
}

// ---------------------------------------------------------------------------
// Main layout function
// ---------------------------------------------------------------------------

/**
 * Computes glyph placements for the given text string.
 *
 * @param text - The input text string. Empty strings are valid and return {glyphs: [], totalWidth: 0, height: h}.
 * @param variant - Font variant ('A' or 'B').
 * @param h - Cap height in output units (e.g., mm).
 * @returns TextLayout with glyphs array, total width, and height.
 */
export function layoutText(text: string, variant: FontVariant, h: number): TextLayout {
    const k = h / 350
    const WIDTHS = variant === 'A' ? WIDTHS_A : WIDTHS_B
    const defaultSpacing = h / STROKE_DIVISOR // stroke-width ≈ 1/7 * h
    const wordSpacing = variant === 'A' ? (5 / STROKE_DIVISOR) * h : (4 / STROKE_DIVISOR) * h

    const chars = Array.from(text) // Handles multi-byte chars correctly

    const glyphs: GlyphPlacement[] = []
    let cursor = 0

    for (let i = 0; i < chars.length; i++) {
        const ch = chars[i]

        // Handle space character
        if (ch === ' ') {
            cursor += wordSpacing
            continue
        }

        // Get advance width for this character
        const rawWidth = WIDTHS[ch] ?? WIDTHS[resolveBase(ch)]
        const width = (rawWidth ?? 0) * k

        glyphs.push({
            char: ch,
            x: cursor,
            width,
            scale: k
        })

        cursor += width

        // Determine spacing to the next non-absent character
        const nextCh = chars[i + 1]
        if (nextCh === undefined) break

        if (nextCh === ' ') {
            // Space will advance cursor on its own turn; no inter-char spacing here
            continue
        }

        // Check digit→unit abbreviation override
        let spacing: number | undefined
        if (isDigit(ch)) {
            spacing = digitUnitSpacing(chars, i, i + 1, variant)
        }

        // Fall back to kerning table
        if (spacing === undefined) {
            const kernVal = kernLookup(ch, nextCh, variant)
            spacing = kernVal !== undefined ? kernVal * k : defaultSpacing
        }

        cursor += spacing
    }

    return {
        glyphs,
        totalWidth: cursor,
        height: h
    }
}
